import { Users, CornerDownRight, History, MoveRight, RotateCcw, Settings } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePermission } from '../../../../auth/AuthContext';
import { Breadcrumb } from '../../../../components/Breadcrumb';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { CreateButton } from '../../../../components/CreateButton';
import { EmptyState } from '../../../../components/EmptyState';
import { ErrorState } from '../../../../components/ErrorState';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { SearchGroup, TOOLBAR_SEARCH_WIDTH } from '../../../../components/index-page/SearchGroup';
import { Menu, type MenuItem } from '../../../../components/Menu';
import { Skeleton } from '../../../../components/Skeleton';
import { RowActionMenu } from '../../../../components/table/RowActionMenu';
import { StatusText } from '../../../../components/table/StatusText';
import { Tooltip } from '../../../../components/Tooltip';
import { TreeChart } from '../../../../components/tree/TreeChart';
import { TreeNodeCard } from '../../../../components/tree/TreeNodeCard';
import { countTree, filterTree } from '../../../../components/tree/treeQuery';
import { treeSettingsItems } from '../../../../components/tree/treeSettingsItems';
import { laneOf } from '../lanes';
import { useTierLadder } from '../useTierLadder';
import { AddTierButton } from '../components/AddTierButton';
import { LaneActions } from '../components/LaneActions';
import { MoveTierModal } from '../components/MoveTierModal';
import { OrgUnitTypeModal } from '../components/OrgUnitTypeModal';
import { useTreeOrientation } from '../../../../components/tree/useTreeOrientation';
import { suggestChildCode } from '../suggestCode';
import { ApiError } from '../../../../lib/apiError';
import { OrgUnitModal } from '../components/OrgUnitModal';
import { orgUnitTypesApi, orgUnitsApi } from '../organizationApi';
import type { OrgUnitTreeNode, OrgUnitTypeListItem } from '../organizationTypes';
import { UnitEmployeesModal } from '../../personnel/components/UnitEmployeesModal';
import { useUnitHeadcounts } from '../../personnel/useUnitHeadcounts';

/** A stable reference for "nothing loaded yet", so the search memo is not defeated. */
const NO_NODES: OrgUnitTreeNode[] = [];

/**
 * Chart — the organizational chart, edited in place.
 *
 * **Not an `<IndexPage>`.** The index shell is right when a module's records are a flat list
 * of peers; these are not. A unit's position *is* its most important field, and a table can
 * only show it as a `Parent` column — which puts the parent one screen away from its child,
 * makes "what sits under this" a search, and turns moving a branch into a chain of edits
 * against names the reader has to hold in their head. The tree is the record and the
 * navigation at once, so an edit happens where the reader is already looking.
 *
 * What the shell would have brought is here in the shapes a tree can carry it: search
 * filters the branches, the record count sits in the same `<SearchGroup>`, the settings cog
 * holds the orientation, History Logs and Clear data, and every node's kebab is the shared
 * `<RowActionMenu>` — so an action opens the same menu, in the same order, with the same
 * permission filtering as a row anywhere else in the product.
 *
 * **Employees is the entry this used to say could not exist.** It read "no 'Manage People'
 * entry — this system has no personnel record and no link between an account and a unit to
 * hang it on", and that was true until the Personnel phase landed. A unit now has employees,
 * reached through `personnelApi.list`’s own `unit` facet, and the kebab opens them.
 *
 * It carries `personnel.read` — the key of what it opens, never the key of the page it sits
 * on — so a reader who may edit the chart but not read personnel does not see the entry. It
 * is deliberately NOT an implied read: `org-units` → `personnel.read` would hand the whole
 * personnel module to everyone who can look at the chart.
 */
export function OrgChartPage() {
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [orientation, setOrientation] = useTreeOrientation('org-units');
  /*
    Gated on the key the Employees entry itself carries, so a reader without it makes no
    request at all — a count of another module’s records must not reach someone who may not
    read them, and hiding the badge would leave the number in the network tab.
  */
  const canReadPersonnel = usePermission('personnel.read');
  const headcounts = useUnitHeadcounts(canReadPersonnel);
  const [reloadKey, setReloadKey] = useState(0);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [editing, setEditing] = useState<OrgUnitTreeNode | null>(null);
  /**
   * Non-null while the create modal is open, carrying the unit the child is added under.
   *
   * The name travels with the id so the modal's parent field can state it before its own
   * options feed arrives — without it the field renders its placeholder for the first
   * moment, which reads as "no parent chosen" on a create opened from a specific node.
   */
  const [creating, setCreating] = useState<{
    parent: { id: string; name: string; tierSortOrder: number } | null;
  } | null>(null);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    unit: OrgUnitTreeNode;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [actionError, setActionError] = useState<ApiError | null>(null);

  const [clearOpen, setClearOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [tierOpen, setTierOpen] = useState(false);
  /** The unit whose tier is being moved, or null. */
  const [moving, setMoving] = useState<OrgUnitTreeNode | null>(null);
  /** The unit whose employees are being listed, or null. */
  const [employeesFor, setEmployeesFor] = useState<{ id: string; name: string } | null>(null);
  /**
   * The tier being edited from its column header.
   *
   * Fetched rather than assembled from the node that named the lane: a tree node carries the
   * tier's id, name and rank but not its description, and saving a form seeded from that
   * would blank a field the reader never opened.
   */
  const [editingTier, setEditingTier] = useState<OrgUnitTypeListItem | null>(null);
  /** The tier being deleted from its column header, with what the confirmation needs to say. */
  const [deletingTier, setDeletingTier] = useState<{
    id: string;
    name: string;
    unitCount: number;
  } | null>(null);
  const [tierBusy, setTierBusy] = useState(false);
  /*
    The ladder the columns are drawn against, reloaded with the chart — creating, editing or
    moving a tier changes the columns, not just the cards.
  */
  const { lanes, tierAt } = useTierLadder(reloadKey);

  /*
    One piece of state carrying what was loaded AND which request loaded it, so `loading` is
    derived during render rather than set synchronously at the top of the effect. Flipping
    Show deleted changes the request, which makes the held result stale, which is the same
    thing as loading — a second flag would be a way for the two to disagree.
  */
  const [result, setResult] = useState<{
    forDeleted: boolean;
    forReload: number;
    nodes: OrgUnitTreeNode[];
    error: ApiError | null;
  } | null>(null);

  const loading =
    result === null || result.forDeleted !== includeDeleted || result.forReload !== reloadKey;
  // A stable empty array, not a fresh `[]` per render — the search memo below keys off
  // `nodes`, and a new reference every render would defeat it while the chart is loading.
  const nodes = result?.nodes ?? NO_NODES;
  const error = loading ? null : result.error;

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  // Debounced to 300ms, matching every other search in the product.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const controller = new AbortController();

    orgUnitsApi
      .tree(includeDeleted, controller.signal)
      .then((tree) =>
        setResult({ forDeleted: includeDeleted, forReload: reloadKey, nodes: tree, error: null }),
      )
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setResult({
          forDeleted: includeDeleted,
          forReload: reloadKey,
          nodes: [],
          error:
            caught instanceof ApiError ? caught : new ApiError('The chart could not be loaded.', 0),
        });
      });

    return () => controller.abort();
  }, [includeDeleted, reloadKey]);

  const term = search.trim().toLowerCase();

  const shown = useMemo(
    () =>
      term
        ? filterTree(nodes, (node) =>
            [node.name, node.code, node.orgUnitTypeName].some((field) =>
              field?.toLowerCase().includes(term),
            ),
          )
        : nodes,
    [nodes, term],
  );

  const total = useMemo(() => countTree(shown), [shown]);

  /*
    The tree is remounted on a search or a Show deleted change, so every branch reopens.
    `<TreeChart>` keys its collapse state by node id, and a filtered result is a different
    set of nodes — a reader who searches expects to see the hit, not a hit folded inside a
    branch they had collapsed before typing.
  */
  const treeKey = `${term}|${includeDeleted}`;

  const settingsItems: MenuItem[] = [
    // Shared with Preview: the orientation pair, then Show deleted. Every filter this
    // surface has lives in here, which is the one place a reader looks for them.
    ...treeSettingsItems({
      orientation,
      onOrientation: setOrientation,
      includeDeleted,
      onIncludeDeleted: setIncludeDeleted,
    }),
    {
      key: 'history-logs',
      label: 'History Logs',
      icon: <History className="h-4 w-4" aria-hidden="true" />,
      permission: 'audit-logs.read',
      divider: true,
      onClick: () => setHistoryOpen(true),
    },
    {
      key: 'clear-data',
      label: 'Clear data',
      // Its own key, never implied by `.delete`: deleting the unit in front of you and
      // deleting the whole chart are different privileges.
      permission: 'org-units.clear-data',
      destructive: true,
      divider: true,
      onClick: () => setClearOpen(true),
    },
  ];

  /** Loads the tier behind a column before opening its form. */
  async function openTier(id: string) {
    setTierBusy(true);

    try {
      setEditingTier(await orgUnitTypesApi.get(id));
    } catch (caught) {
      setActionError(
        caught instanceof ApiError ? caught : new ApiError('Could not load that tier.', 0),
      );
    } finally {
      setTierBusy(false);
    }
  }

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setActionError(null);

    try {
      if (confirming.kind === 'deactivate') await orgUnitsApi.deactivate(confirming.unit.id);
      else await orgUnitsApi.activate(confirming.unit.id);

      setConfirming(null);
      refresh();
    } catch (caught) {
      setActionError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-50">
      <Breadcrumb segments={['Human Resources', 'Organization', 'Units/Chart']} />

      {/* The index shell's toolbar geometry, so the two surfaces read as one product. */}
      <div className="flex shrink-0 items-center gap-2 border-b border-slate-200 px-4 py-2.5">
        {/*
          The shared button, not a copy of it. A tree is this module's index surface, so its
          Create is the product's one Create — the gate, the glyph and the disabled reason are
          the component's, and this toolbar owns only where it sits.
        */}
        <CreateButton
          id="org-units-create"
          onClick={() => setCreating({ parent: null })}
          permission="org-units.create"
        />

        <SearchGroup
          id="org-units"
          value={searchInput}
          onChange={setSearchInput}
          placeholder="Search units"
          total={total}
          loading={loading}
          // Same width rule as the index shell's toolbar — this row copies its geometry.
          className={TOOLBAR_SEARCH_WIDTH}
        >
          <Menu
            id="org-units-settings-menu"
            items={settingsItems}
            trigger={({ open, toggle }) => (
              <Tooltip label="Chart settings">
                <button
                  type="button"
                  id="org-units-settings"
                  onClick={toggle}
                  aria-haspopup="menu"
                  aria-expanded={open}
                  aria-label="Chart settings"
                  className="flex h-full items-center rounded-r-lg border-l border-slate-300 px-3 text-slate-500 transition hover:bg-slate-50 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-200"
                >
                  <Settings className="h-4 w-4" aria-hidden="true" />
                </button>
              </Tooltip>
            )}
          />
        </SearchGroup>

      </div>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 p-4">
        {/*
          The chart is as wide as the deepest branch, so it owns its own scroll region in
          both directions and the page body never scrolls.
        */}
        {/*
          No padding: the chart IS a grid, and its rail, its header and its rules are meant to
          meet the panel's own edge. Padding left a band of nothing between the border and the
          first column, which read as the grid being adrift inside the panel rather than being
          the panel's content.
        */}
        <div className="modern-scrollbar flex min-h-0 flex-1 overflow-auto rounded-xl border border-slate-200 bg-white">
          {loading ? (
            <div className="m-auto flex flex-col gap-3">
              {Array.from({ length: 4 }, (_, i) => (
                <Skeleton key={i} className="h-10 w-72" />
              ))}
            </div>
          ) : error ? (
            <div className="m-auto">
              <ErrorState
                message={error.message}
                correlationId={error.correlationId}
                onRetry={refresh}
              />
            </div>
          ) : shown.length === 0 ? (
            <div className="m-auto">
              {/*
                The standard empty panel: centred, with the module's own Create button in it.
                An empty list whose only route to a first record is a toolbar button the
                reader has to go looking for is a dead end wearing a sentence.
              */}
              <EmptyState
                message={emptyMessage(term, includeDeleted, nodes.length)}
                action={
                  term
                    ? undefined
                    : {
                        label: 'Create unit',
                        permission: 'org-units.create',
                        onClick: () => setCreating({ parent: null }),
                      }
                }
              />
            </div>
          ) : (
            <TreeChart
              key={treeKey}
              nodes={shown}
              orientation={orientation}
              label="Organizational chart"
              lanes={lanes}
              laneOf={laneOf}
              laneAction={<AddTierButton onClick={() => setTierOpen(true)} />}
              renderLaneActions={(lane) => {
                /*
                  Resolved from the ladder, not from a record in the column: a column may be
                  empty, and it still has a tier to edit — which is the whole reason the
                  columns come from the ladder.
                */
                const tier = tierAt(lane.key);

                if (!tier) {
                  return null;
                }

                return (
                  <LaneActions
                    tierId={tier.id}
                    label={tier.name}
                    isLast={lane.isLast}
                    unitCount={lane.count}
                    onEdit={() => void openTier(tier.id)}
                    onDelete={() =>
                      setDeletingTier({
                        id: tier.id,
                        name: tier.name,
                        unitCount: lane.count,
                      })
                    }
                  />
                );
              }}
              getKey={(node) => node.id}
              getChildren={(node) => node.children}
              renderNode={(node, state) => (
                <TreeNodeCard
                  name={node.name}
                  // Smaller inside a sub-tier — its parent holds the same rank.
                  sub={state.sub}
                  muted={!node.isActive}
                  status={
                    node.isActive ? undefined : <StatusText tone="inactive">Deleted</StatusText>
                  }
                  actions={
                    <RowActionMenu
                      id={`org-unit-${node.id}-actions`}
                      /*
                        Audit metadata lives in the popover, not on the card — the chart is
                        read for its structure, and four timestamps per node would bury it.
                      */
                      otherInfo={node}
                      extraItems={[
                        {
                          /*
                            First, and above the verbs: it is the only entry that just LOOKS
                            at the unit. The key is `personnel.read` — the key of what the
                            entry opens, not of the page it sits on — so a reader who may
                            edit the chart but not read personnel does not see it at all.
                          */
                          key: `employees-${node.id}`,
                          label: 'Employees',
                          badge: headcounts.countOf(node.id),
                          icon: <Users className="h-4 w-4" aria-hidden="true" />,
                          permission: 'personnel.read',
                          onClick: () => setEmployeesFor({ id: node.id, name: node.name }),
                        },
                        {
                          key: `add-child-${node.id}`,
                          label: 'Add child unit',
                          icon: <CornerDownRight className="h-4 w-4" aria-hidden="true" />,
                          permission: 'org-units.create',
                          // State, so it says why: the parent feed offers active units only,
                          // and the server will not file a live unit under a deleted one.
                          disabledReason: node.isActive
                            ? null
                            : 'Restore this unit before adding anything under it.',
                          onClick: () =>
                            setCreating({
                              parent: {
                                id: node.id,
                                name: node.name,
                                // The parent's rung, so the form can default to the next one.
                                tierSortOrder: node.orgUnitTypeSortOrder,
                              },
                            }),
                        },
                        {
                          key: `move-tier-${node.id}`,
                          label: 'Move to tier',
                          icon: <MoveRight className="h-4 w-4" aria-hidden="true" />,
                          // The key of the write it performs: it changes a field on this
                          // record, which is an update.
                          permission: 'org-units.update',
                          disabledReason: node.isActive
                            ? null
                            : 'Restore this unit before moving it.',
                          onClick: () => setMoving(node),
                        },
                        {
                          key: `restore-${node.id}`,
                          label: 'Restore',
                          icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
                          permission: 'org-units.delete',
                          disabledReason: node.isActive ? 'This unit is already active.' : null,
                          onClick: () => setConfirming({ kind: 'activate', unit: node }),
                        },
                      ]}
                      onEdit={() => setEditing(node)}
                      editPermission="org-units.update"
                      onDelete={() => setConfirming({ kind: 'deactivate', unit: node })}
                      deletePermission="org-units.delete"
                      /*
                        Stated, not hidden. A unit with live children genuinely cannot be
                        deleted yet, and the count is the actionable part — it says how much
                        has to move first.
                      */
                      deleteDisabledReason={
                        !node.isActive
                          ? 'This unit is already deleted.'
                          : node.childCount > 0
                            ? `Move or delete its ${node.childCount} child unit${node.childCount === 1 ? '' : 's'} first.`
                            : null
                      }
                    />
                  }
                />
              )}
            />
          )}
        </div>
      </div>

      {creating !== null || editing !== null ? (
        <OrgUnitModal
          open
          editing={editing}
          defaultParent={creating?.parent ?? null}
          /*
            The suggester reads the whole loaded chart, because the next free code under a
            parent is a fact about that parent's existing children rather than about the unit
            being created — the modal cannot work it out from what it holds.
          */
          suggestCode={(parentId) => suggestChildCode(nodes, parentId)}
          onClose={() => {
            setCreating(null);
            setEditing(null);
          }}
          onSaved={(result) => {
            // `keepOpen` is the reader adding a run of units: refresh the chart behind the
            // modal, but leave `creating` set or the form unmounts under them mid-run.
            if (!result?.keepOpen) {
              setCreating(null);
              setEditing(null);
            }
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setActionError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete unit' : 'Restore unit'}
          danger={confirming.kind === 'deactivate'}
          /*
            Both safeguards on the destructive half only. Restoring a record needs no
            ceremony — it is the undo.
          */
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`org-unit-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `"${confirming.unit.name}" will leave the chart. The record is kept and can be restored.`
                  : `"${confirming.unit.name}" will appear in the chart again, under ${confirming.unit.parentName ?? 'the top level'}.`}
              </p>
              {actionError ? (
                <p
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
                >
                  {actionError.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}

      {/*
        The consequence is stated on the form, before the tick — it says what will happen,
        that it is reversible, and exactly how to reverse it, because a reader deciding
        whether to do this needs the way back in front of them rather than in a runbook.
      */}
      <ConfirmDialog
        open={clearOpen}
        onClose={() => setClearOpen(false)}
        id="org-units-clear-data-dialog"
        title="Clear all organizational units"
        danger
        requireConfirm
        requireCredential
        loading={clearing}
        body={
          <div className="space-y-2">
            <p>Every active unit in the chart will be deleted at once.</p>
            <p>
              Nothing is erased. Deleted units stay in the database and come back into view
              through <span className="font-medium text-slate-700">Show deleted</span> in the
              chart settings, from where each one can be restored.
            </p>
          </div>
        }
        onConfirm={async () => {
          setClearing(true);
          try {
            await orgUnitsApi.clear();
            setClearOpen(false);
            refresh();
          } finally {
            setClearing(false);
          }
        }}
      />

      {editingTier ? (
        <OrgUnitTypeModal
          open
          editing={editingTier}
          onClose={() => setEditingTier(null)}
          onSaved={() => {
            setEditingTier(null);
            // A tier's rank orders the lanes, so editing one can reorder the whole chart.
            refresh();
          }}
        />
      ) : null}

      {deletingTier ? (
        <ConfirmDialog
          open
          onClose={() => setDeletingTier(null)}
          id="tier-delete-dialog"
          title="Delete tier"
          danger
          requireConfirm
          requireCredential
          loading={tierBusy}
          body={
            <div className="space-y-2">
              <p>
                The <span className="font-medium text-slate-700">{deletingTier.name}</span> tier
                will be deleted, and its column will leave the chart.
              </p>
              {deletingTier.unitCount > 0 ? (
                <p>
                  {deletingTier.unitCount} unit{deletingTier.unitCount === 1 ? '' : 's'} still
                  carr{deletingTier.unitCount === 1 ? 'ies' : 'y'} it. They are not deleted and
                  keep resolving its name, but they will have no tier to draw in until they are
                  moved.
                </p>
              ) : null}
              <p>Nothing is erased — a deleted tier can be restored from Tiers.</p>
            </div>
          }
          onConfirm={async () => {
            setTierBusy(true);
            try {
              await orgUnitTypesApi.deactivate(deletingTier.id);
              setDeletingTier(null);
              refresh();
            } finally {
              setTierBusy(false);
            }
          }}
        />
      ) : null}

      {employeesFor ? (
        <UnitEmployeesModal
          open
          unit={employeesFor}
          onClose={() => setEmployeesFor(null)}
        />
      ) : null}

      {moving ? (
        <MoveTierModal
          open
          unit={moving}
          onClose={() => setMoving(null)}
          onMoved={() => {
            setMoving(null);
            // Lanes are derived from the tiers on screen, so a move can add or remove a
            // whole column — the chart is refetched rather than patched.
            refresh();
          }}
        />
      ) : null}

      {tierOpen ? (
        <OrgUnitTypeModal
          open
          editing={null}
          onClose={() => setTierOpen(false)}
          onSaved={() => {
            setTierOpen(false);
            // The new tier has no units yet, so no lane appears — but the type pickers behind
            // this chart do change, and the reload keeps them honest.
            refresh();
          }}
        />
      ) : null}

      <HistoryLogsModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        module="org-units"
        entityTypes={['OrgUnit']}
      />
    </div>
  );
}

/**
 * Why the chart is empty, in the reader's terms.
 *
 * Three different situations produce no nodes, and one sentence for all three would be wrong
 * twice: a filtered-out chart is not an empty one, and a chart holding only deleted units is
 * not a chart with nothing in it.
 */
function emptyMessage(term: string, includeDeleted: boolean, loaded: number): string {
  if (term) {
    return `No unit matches "${term}". Search reads a unit's name, its code, and its tier.`;
  }

  if (!includeDeleted && loaded === 0) {
    return 'No active units. Turn on Show deleted in the chart settings to see the deleted ones.';
  }

  return 'No units yet. Create the top-level ones first — a unit can only sit under one that already exists.';
}
